<template>
    <div
        class="absolute bottom-5 right-6 z-50 text-sm font-medium text-slate-800"
        v-if="props.totalPages > 1"
    >
        <ResultsCount
            pClass="w-fit text-slate-800 leading-tight text-xs bg-slate-200 border border-gray-300 rounded-t shadow-sm text-center px-1.5 py-1 w-full font-normal"
            :startRange="startRange"
            :endRange="endRange"
            :totalResults="totalResults"
        />
        <nav
            class="flex rounded-b text-slate-800 font-normal text-sm bg-white items-center -space-x-px"
            aria-label="Pagination"
        >
            <button
                @click="prev"
                :disabled="isInFirstPage"
                class="h-9 flex flex-grow py-2 px-1.5 rounded-bl items-center justify-center min-w-9 leading-tight border-2 border-transparent disabled:!text-slate-800 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 active:bg-slate-200 focus:border-slate-300 focus:outline-0"
            >
                <span class="sr-only">Previous</span>
                <svg
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                        clip-rule="evenodd"
                    />
                </svg>
            </button>
            <button
                v-for="page in pages"
                :key="page.name"
                @click="onClickPage(page.name)"
                aria-current="page"
                class="h-9 flex p-2 flex-grow items-center justify-center min-w-9 leading-tight border-2 border-transparent focus:z-20 focus:border-slate-300 focus:outline-0 active:border-transparent"
                :class="{
                    'bg-slate-800 text-white hover:bg-slate-700 active:bg-slate-700':
                        currentPage === Number(page.name),
                    'text-slate-800 hover:bg-slate-200 active:bg-slate-200':
                        currentPage !== Number(page.name),
                }"
            >
                {{ page.name }}
            </button>
            <button
                @click="next"
                :disabled="isInLastPage"
                class="h-9 flex flex-grow py-2 px-1.5 rounded-br items-center justify-center min-w-9 leading-tight border-2 border-transparent disabled:!text-slate-800 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 active:bg-slate-200 focus:border-slate-300 focus:outline-0"
            >
                <span class="sr-only">Next</span>
                <svg
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fill-rule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clip-rule="evenodd"
                    />
                </svg>
            </button>
        </nav>
    </div>
</template>
<script setup>
import { computed } from 'vue';
import ResultsCount from '@/components/ui/results-count/results-count.vue';

const props = defineProps({
    currentPage: {
        type: Number,
        default: 1,
    },
    totalPages: {
        type: Number,
        default: 1,
    },
    maxVisibleButtons: {
        type: Number,
        default: 3,
    },
    perPage: {
        type: Number,
        required: true,
    },
    totalResults: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['pageChanged']);

const startRange = computed(() => {
    return (props.currentPage - 1) * props.perPage + 1;
});

const endRange = computed(() => {
    const end = props.currentPage * props.perPage;
    return end > props.totalResults ? props.totalResults : end;
});

const startPage = computed(() => {
    if (props.currentPage === 1) {
        return 1;
    }
    if (props.currentPage === props.totalPages) {
        const start = props.totalPages - (props.maxVisibleButtons - 1);

        if (start <= 1) {
            return 1;
        } else {
            return start;
        }
    }
    return props.currentPage - 1;
});

const pages = computed(() => {
    const range = [];

    for (
        let i = startPage.value;
        i <=
        Math.min(
            startPage.value + props.maxVisibleButtons - 1,
            props.totalPages
        );
        i++
    ) {
        range.push({
            name: i,
            isDisabled: i === props.currentPage,
        });
    }
    return range;
});

const isInFirstPage = computed(() => props.currentPage === 1);
const isInLastPage = computed(() => props.currentPage === props.totalPages);

const prev = () => {
    emit('pageChanged', props.currentPage - 1);
};

const onClickPage = (page) => {
    emit('pageChanged', page);
};

const next = () => {
    emit('pageChanged', props.currentPage + 1);
};

// const onClickLastPage = () => {
//     emit('pageChanged', props.totalPages);
// };
// const onClickFirstPage = () => {
//     emit('pageChanged', 1);
// };
</script>
